import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router';

import type { Pagination } from '@/common/pagination';
import { ROLE } from '@/common/role';
import { Restricted } from '@/components/Restricted';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDebounceValue } from '@/hooks/use-debounce-value';
import { usePermissionMetadata } from '@/hooks/use-permission';
import { type AssetListQueryFilterType, getListAssets } from '@/queries/assets/list-assets';
import { getListOrganizationsOption } from '@/queries/organizations/list-organizations';
import type { ProfileLoaderType } from '@/routes/loader/profile';
import type { AssetWithStats } from '../../../common/asset';
import { cn } from '../../../lib/utils';
import { SelectOrganizationPopover } from '../../Organizations/SelectOrganizationPopup';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../../ui/resizable';
import { AssetListMapCard } from './map/AssetListMapCard';
import { AssetsMapContainer } from './map/AssetsMapContainer';

export const AssetsPageV3 = () => {
	const { t } = useTranslation();
	const { profile } = (useLoaderData() as ProfileLoaderType) || {};
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useDebounceValue(searchTerm, 500);
	const [organizationFilter, setOrganizationFilter] = useState<string>('all');
	const [filters, setFilters] = useState({} as AssetListQueryFilterType);
	const [pagination, _setPagination] = useState({
		limit: 100,
		offset: 0,
	} as Pagination);
	const { isSuperAdmin } = usePermissionMetadata();
	const { data: assetsResponse, isLoading } = useQuery({
		queryKey: ['assets', 'list', filters, pagination],
		queryFn: () => getListAssets({ ...filters, ...pagination }),
		enabled: !!profile,
	});
	const { data: orgListResponse } = useQuery(getListOrganizationsOption({ enabled: isSuperAdmin }));
	const organizations = orgListResponse?.organizations || [];
	const navigate = useNavigate();
	const assets = assetsResponse?.assets || [];
	const [selectedAsset, setSelectedAsset] = useState<AssetWithStats | null>(null);
	const listCardRef = useRef<Record<string, HTMLDivElement | null>>({});

	useEffect(() => {
		const newFilters: AssetListQueryFilterType = {};
		if (debouncedSearch) {
			newFilters.search = debouncedSearch;
		}
		if (organizationFilter !== 'all') {
			newFilters.organizationId = organizationFilter;
		}
		setFilters(newFilters);
	}, [debouncedSearch, organizationFilter]);

	const handleSearchTermInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchTerm(value);
		setDebouncedSearch(value);
	};

	const onSelectAsset = useCallback((asset: AssetWithStats) => {
		setSelectedAsset(asset);
		listCardRef.current[asset.id || '']?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, []);

	return (
		<div className={cn('w-full mx-auto flex flex-col relative h-[calc(100vh-64px)] overflow-hidden')}>
			{!isLoading && (
				<ResizablePanelGroup direction="horizontal" className="border-t border-zinc-200">
					<ResizablePanel defaultSize={30} minSize={25} maxSize={40}>
						<div className="flex flex-col h-full bg-zinc-50/50">
							<div className="flex-none p-3 border-b border-zinc-200 bg-white shadow-sm z-10">
								<div className="flex flex-row items-center gap-2">
									<div className="relative flex-1">
										<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
										<Input
											type="text"
											className="flex h-10 w-full rounded-md border pl-9 bg-zinc-50 focus-visible:bg-white transition-colors"
											placeholder={t('assets.list.searchPlaceholder')}
											value={searchTerm}
											onChange={handleSearchTermInput}
											autoComplete="off"
										/>
									</div>
									{isSuperAdmin && (
										<div className="relative w-auto shrink-0">
											<SelectOrganizationPopover
												asFilter
												addAllItem
												inactiveToggle
												organizations={organizations}
												onChange={(value) => setOrganizationFilter(value)}
												value={organizationFilter}
											/>
										</div>
									)}
									<Restricted to={ROLE.ASSETS_CREATE}>
										<Button
											size="icon"
											className="shrink-0 h-10 w-10 bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-sm transition-all"
											onClick={() => navigate('/assets/new')}
											title={t('assets.list.createNewAsset')}
										>
											<Plus className="size-5" />
										</Button>
									</Restricted>
								</div>
							</div>

							<div className="flex-1 overflow-y-auto p-3 space-y-3">
								{assets.length > 0 ? (
									assets.map((asset) => (
										<AssetListMapCard
											ref={(el) => {
												listCardRef.current[asset.id || ''] = el;
											}}
											key={asset.id}
											asset={asset}
											onSelectAsset={onSelectAsset}
											selectedAssetId={selectedAsset?.id}
										/>
									))
								) : (
									<div className="flex flex-col items-center justify-center h-32 text-zinc-400">
										<p className="text-sm font-medium">{t('assets.list.empty')}</p>
									</div>
								)}
							</div>
						</div>
					</ResizablePanel>

					<ResizableHandle className="w-1 bg-zinc-200 hover:bg-blue-400 active:bg-blue-600 transition-colors duration-200" />

					<ResizablePanel collapsible minSize={30}>
						<div className="flex-1 flex overflow-hidden m-0 h-full w-full bg-zinc-100">
							<AssetsMapContainer assets={assets} selectedAsset={selectedAsset} onSelectAsset={onSelectAsset} />
						</div>
					</ResizablePanel>
				</ResizablePanelGroup>
			)}
		</div>
	);
};
